import { type ComponentProps } from 'react'
import { Collapsible } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'

interface SidepanelSectionTitleGroupProps extends ComponentProps<typeof Collapsible.Trigger> {
  /**
   * A heading that toggles nothing: the same lockup with no hover state and no
   * chevron, rendered as a plain element rather than a `Collapsible.Trigger`.
   */
  static?: boolean
}

const TITLE_CLASS =
  'text-foreground relative flex min-w-0 items-center justify-start gap-2 self-stretch text-sm font-medium leading-none'

const INTERACTIVE_CLASS = twMerge(
  'group/title cursor-pointer bg-transparent',
  // `self-stretch` + an `inset-y-0` pseudo take the hit area to the header's
  // top and bottom edges without hard-coding its height. `-left-3` matches the
  // header's `px-3`; `-right-2` stops short of the trailing slot.
  "after:absolute after:inset-y-0 after:-left-3 after:-right-2 after:rounded-l-[var(--radius-size-md)] after:corner-squircle after:content-['']",
)

const CHEVRON_CLASS =
  'text-foreground-secondary -ml-0.5 size-4 shrink-0 transition-transform duration-200 group-hover/title:text-foreground motion-reduce:transition-none'

/**
 * Icon + label + badge + chevron — and the section's collapse trigger, so the
 * whole title is one target rather than a 14px chevron beside a button that
 * does something else.
 *
 * Must be rendered inside a `Collapsible` root unless `static`; `justify-start`
 * overrides the shared trigger's `justify-between`, which would otherwise
 * strand the chevron if the trigger were ever given a width.
 */
export function SidepanelSectionTitleGroup({
  static: isStatic = false,
  className,
  iconClassName,
  children,
  ...props
}: SidepanelSectionTitleGroupProps) {
  if (isStatic) {
    return <div className={twMerge(TITLE_CLASS, className)}>{children}</div>
  }

  return (
    <Collapsible.Trigger
      className={twMerge(TITLE_CLASS, INTERACTIVE_CLASS, className)}
      iconClassName={twMerge(CHEVRON_CLASS, iconClassName)}
      {...props}
    >
      {children}
    </Collapsible.Trigger>
  )
}
